<template>
  <box>
    <div class="position-relative">
      <b-row class="mb-4">
        <b-col lg="5" class="segment-form-left">
          <b-form-group class="mb-0">
            <multi-select
              v-if="editing"
              :search="values.length > 9"
              :select-options="segmentsOptions"
              btn-label="Segments"
              v-model="selectedSegment"
              class="mb-2"
            />
            <b-form-input v-model="segmentName" placeholder="Segment Name" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col lg="5" class="mb-4 segment-form-left">
          <div class="segment-list" v-if="!isMobileLayout">
            <b-list-group>
              <b-list-group-item
                :key="dimension.name"
                :class="{
                  'd-flex justify-content-between align-items-center': true,
                  active: dimension.id === selectedDimension.id
                }"
                href="#"
                v-for="dimension in dimensionsList"
                @click.prevent="showDetails(dimension)"
              >
                {{ dimension.name }}
                <b-badge
                  v-if="selected[dimension.id] && selected[dimension.id].values"
                  variant="primary"
                  pill
                >
                  {{ selected[dimension.id].values.length }}
                </b-badge>
                <b-badge
                  v-if="selected[dimension.id] && selected[dimension.id].range"
                  variant="success"
                  pill
                >
                  <svg-icon icon="check" class="icon-xs" />
                </b-badge>
              </b-list-group-item>
            </b-list-group>
          </div>
          <b-form-group v-else>
            <multi-select
              :search="dimensionsList.length > 9"
              :select-options="dimensionsList"
              btn-label="Dimensions"
              @input="showDetails"
            />
          </b-form-group>
        </b-col>
        <b-col lg="7" class="segment-form-right">
          <h3 class="mb-4" v-if="selectedDimension">
            {{ selectedDimension.name }}
          </h3>
          <div
            v-if="
              selectedDimension &&
                selectedDimension.configuration.excludable &&
                selectedDimension.configuration.type !== 'range'
            "
            class="d-flex align-items-center mb-4"
          >
            <b-form-radio-group
              v-model="selectedDimension.isExclude"
              :options="[
                { text: 'Exclude', value: true },
                { text: 'Include', value: false }
              ]"
              @input="setDimension"
            />
          </div>
          <multi-select
            v-if="
              values.length &&
                selectedDimension &&
                selectedDimension.configuration.type === 'checkbox'
            "
            :search="values.length > 9"
            :options="{ multi: true }"
            :select-options="values"
            :value="defaultDimensions"
            btn-label="Values"
            @input="setDimension"
          />
          <div
            v-if="
              !values.length &&
                selectedDimension &&
                selectedDimension.configuration.type === 'checkbox'
            "
            class="d-flex align-items-center justify-content-center loader"
          >
            <b-spinner variant="primary" />
          </div>
          <b-row
            class="d-flex"
            v-if="
              selectedDimension &&
                selectedDimension.configuration.type === 'range'
            "
          >
            <b-col>
              <b-form-group class="mb-0">
                <b-form-input
                  type="text"
                  size="md"
                  v-model.number="selectedDimension.range.min"
                  placeholder="Min"
                  @input="setDimension"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group class="mb-0">
                <b-form-input
                  type="text"
                  size="md"
                  v-model.number="selectedDimension.range.max"
                  placeholder="Max"
                  @input="setDimension"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <slot name="main" />
      <div class="d-flex justify-content-end mt-4">
        <b-button
          @click="close"
          class="btn mr-auto"
          variant="secondary"
          size="md"
        >
          {{ $t('close') }}
        </b-button>
        <b-button
          @click="remove"
          class="btn"
          variant="danger"
          size="md"
          v-if="editing && selectedSegment[0]"
        >
          {{ $t('delete') }}
        </b-button>
        <b-button
          @click="save"
          class="btn"
          variant="primary"
          size="md"
          :disabled="!this.segmentName"
        >
          {{ $t('save') }}
        </b-button>
      </div>
    </div>
  </box>
</template>

<script>
import cloneDeep from 'clone';
let cancel;

export default {
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    dimensions: {
      type: Array,
      required: true
    },
    namespace: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      segmentName: '',
      dimensionsList: [],
      selectedDimension: null,
      selectedSegment: [],
      selected: {},
      segments: [],
      segmentsOptions: [],
      values: [],
      lists: {}
    };
  },

  computed: {
    title() {
      return this.editing ? 'Edit segment' : 'Create new segment';
    },

    storageName() {
      return `${this.$store.state.app.role}-${this.namespace}-segments`;
    },

    defaultDimensions() {
      if (!this.selectedDimension || !this.selected[this.selectedDimension.id])
        return [];
      return this.selected[this.selectedDimension.id].values;
    },

    isMobileLayout() {
      return (
        this.$root.screen.md || this.$root.screen.sm || this.$root.screen.xs
      );
    }
  },

  methods: {
    close() {
      this.$emit('close-segment-form');
    },

    getSegments() {
      return JSON.parse(localStorage.getItem(this.storageName));
    },

    getSegment(name) {
      const segments = localStorage.getItem(this.storageName);

      if (!segments) {
        return null;
      }

      const data = JSON.parse(segments);

      return data.find(segment => segment.name === name);
    },

    remove() {
      let segments = this.getSegments();
      segments = segments.filter(item => {
        return item.name !== this.selectedSegment[0].name;
      });
      localStorage.setItem(this.storageName, JSON.stringify(segments));
      this.segmentsOptions = this.getSegments();
      this.segmentName = '';
      this.selected = {};
      this.selectedSegment = [];
      if (!segments.length) {
        this.editing = false;
      }
    },

    save() {
      let segment = this.getSegment(this.segmentName) || {};

      this.segments = this.getSegments() || [];

      segment.name = this.segmentName;
      segment.dimensions = this.selected;

      if (segment.id) {
        this.segments = this.segments.map(item => {
          return item.id === segment.id ? segment : item;
        });
      } else {
        segment.id = this.segments.length + 1;
        this.segments.push(segment);
      }

      localStorage.setItem(this.storageName, JSON.stringify(this.segments));

      this.$notify('alert', {
        text: `Segment ${this.segmentName} successfully saved`,
        variant: 'success',
        time: 5000
      });

      this.close();
    },

    setDimension(data) {
      if (this.selectedDimension) {
        const selectedDimension = this.selected[this.selectedDimension.id]
          ? this.selected[this.selectedDimension.id]
          : cloneDeep(this.selectedDimension);

        if (data instanceof Array) {
          selectedDimension.values = data;
        }

        if (typeof data === 'boolean') {
          selectedDimension.isExclude = data;
        }

        if (typeof data === 'string') {
          selectedDimension.range = this.selectedDimension.range;
        }

        this.$set(this.selected, selectedDimension.id, selectedDimension);

        const isEmptyValues =
          selectedDimension.values && selectedDimension.values.length === 0;
        const isEmptyRange =
          selectedDimension.range &&
          selectedDimension.range.min === null &&
          selectedDimension.range.max === null;

        if (isEmptyValues || isEmptyRange) {
          this.$set(this.selected, selectedDimension.id, null);
          delete this.selected[selectedDimension.id];
        }
      }
    },

    cancelRequest() {
      if (typeof cancel !== 'function') return;
      cancel();
    },

    async showDetails(dimension) {
      if (
        (this.selectedDimension &&
          this.selectedDimension.id === dimension.id) ||
        (dimension instanceof Array && !dimension.length)
      ) {
        return;
      }

      this.values = [];
      let valuesList = [];

      this.selectedDimension =
        dimension instanceof Array ? dimension[0] : dimension;

      if (this.selectedDimension.configuration.type === 'checkbox') {
        if (!this.lists[this.selectedDimension.id]) {
          this.cancelRequest();
          const { values } = await this.$api.adnet.get(
            this.selectedDimension.configuration.options,
            {
              cancelToken: new this.$api.client.CancelToken(function executor(
                c
              ) {
                cancel = c;
              })
            }
          );
          valuesList = values;
          this.lists[this.selectedDimension.id] = values;
        } else {
          valuesList = this.lists[this.selectedDimension.id];
        }
      } else {
        this.selectedDimension.range = {
          min: null,
          max: null
        };
      }

      if (valuesList.length) {
        this.values = valuesList.map(({ id, name }) => {
          return { id, name };
        });
      }
    }
  },

  async created() {
    this.editing = this.isEdit;
    this.dimensionsList = this.dimensions.map(
      ({ key, name, configuration }) => {
        return { id: key, name, configuration, isExclude: false };
      }
    );
    this.segmentsOptions = this.getSegments();
    if (!this.isEdit) {
      const defaultDimension = this.dimensionsList[0];

      await this.showDetails(defaultDimension);
    }
  },

  watch: {
    selectedSegment(val) {
      const selectedSegment = val[0];
      if (selectedSegment) {
        this.segmentName = selectedSegment.name;
        const segment = this.getSegment(selectedSegment.name);
        if (segment.name) {
          this.$set(this, 'selected', segment.dimensions);
        }
      }
    },
    isEdit(val) {
      this.editing = val;
      this.$forceUpdate();
    }
  }
};
</script>

<style lang="scss">
.segments-close-button {
  position: absolute;
  right: 0;
  top: 2px;
  z-index: 1;
  line-height: 1;
  outline: none;

  &:focus {
    outline: none;
  }
}
.segment-form-left {
  max-width: 300px;
}
.segment-form-right {
  max-width: 600px;
}
.segment-list {
  max-height: 320px;
  overflow: auto;
  border-top: 1px solid $border-color;
  border-bottom: 1px solid $border-color;
  border-radius: 0.25rem;

  a {
    &:first-child {
      border-top: none;
    }
    &:last-child {
      border-bottom: none;
    }
  }

  .badge {
    padding: 0.25em 0.4em;
    font-size: 75%;
  }
}
.list-group-item {
  padding: 0.375rem 0.75rem;

  &.active {
    background-color: $primary;
    border-color: $primary;
  }
}
.loader {
  height: calc(1.5em + 0.75rem + 2px);
}
@include media-breakpoint-down(md) {
  .segment-form-left,
  .segment-form-right {
    max-width: 100%;
  }
}
</style>
